/**
 * AcceptJSPaymentForm — Credit card checkout via Authorize.net Accept.js
 * 
 * This component NEVER sees raw card data on our server.
 * Accept.js tokenizes the card client-side and gives us an opaque nonce,
 * which we send to our backend.
 * 
 * Flow:
 * 1. User enters card details in this form
 * 2. Accept.js sends card data directly to Auth.net (not our server)
 * 3. Auth.net returns an opaque payment nonce
 * 4. We send the nonce to our backend /api/payment/* endpoints
 * 5. Backend charges the card via the nonce
 */

import React, { useState, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Box,
  Button,
  Input,
  FormControl,
  FormLabel,
  Icon,
  useToast,
  Alert,
  AlertIcon,
  Divider,
  Badge,
  Spinner,
} from '@chakra-ui/react';
import { FaLock, FaCreditCard, FaCoins, FaCheckCircle } from 'react-icons/fa';
import apiClient from '../../services/api';

const AcceptJSPaymentForm = ({ isOpen, onClose, tierData, paymentType, onSuccess }) => {
  // paymentType: 'subscribe' | 'topup'
  // tierData: { id, name, amount, muAllocation, color }

  const [cardNumber, setCardNumber] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [cvv, setCvv] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const toast = useToast();

  const resetForm = useCallback(() => {
    setCardNumber('');
    setExpMonth('');
    setExpYear('');
    setCvv('');
    setError('');
    setSuccess(false);
    setIsProcessing(false);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  /**
   * Format card number with spaces (4444 4444 4444 4444)
   */
  const formatCardNumber = (value) => {
    const digits = value.replace(/\D/g, '').substring(0, 16);
    const groups = digits.match(/.{1,4}/g);
    return groups ? groups.join(' ') : digits;
  };

  /**
   * Step 1: Send card data to Accept.js for tokenization
   * This calls Auth.net directly from the browser — card data never touches our server
   */
  const getPaymentNonce = () => {
    return new Promise((resolve, reject) => {
      // Check Accept.js is loaded
      if (!window.Accept) {
        reject(new Error('Payment system not loaded. Please refresh the page and try again.'));
        return;
      }

      const authData = {
        clientKey: process.env.REACT_APP_AUTHNET_PUBLIC_CLIENT_KEY,
        apiLoginID: process.env.REACT_APP_AUTHNET_API_LOGIN_ID,
      };

      const cardData = {
        cardNumber: cardNumber.replace(/\s/g, ''),
        month: expMonth.padStart(2, '0'),
        year: expYear.length === 2 ? '20' + expYear : expYear,
        cardCode: cvv,
      };

      const secureData = { authData, cardData };

      window.Accept.dispatchData(secureData, (response) => {
        if (response.messages.resultCode === 'Error') {
          const errMsg = response.messages.message.map(m => m.text).join('. ');
          reject(new Error(errMsg));
        } else {
          resolve({
            dataDescriptor: response.opaqueData.dataDescriptor,
            dataValue: response.opaqueData.dataValue,
          });
        }
      });
    });
  };

  /**
   * Step 2: Send nonce to our backend
   */
  const processPayment = async () => {
    setError('');
    setIsProcessing(true);

    try {
      // Validate inputs
      const rawCardNum = cardNumber.replace(/\s/g, '');
      if (rawCardNum.length < 13 || rawCardNum.length > 16) {
        throw new Error('Please enter a valid card number');
      }
      if (!expMonth || parseInt(expMonth) < 1 || parseInt(expMonth) > 12) {
        throw new Error('Please enter a valid expiration month (01-12)');
      }
      if (!expYear || expYear.length < 2) {
        throw new Error('Please enter a valid expiration year');
      }
      if (!cvv || cvv.length < 3) {
        throw new Error('Please enter a valid CVV');
      }

      // Step 1: Get payment nonce from Accept.js
      const opaqueData = await getPaymentNonce();

      // Step 2: Send to our backend
      let endpoint, body;
      if (paymentType === 'subscribe') {
        endpoint = '/api/payment/subscribe';
        body = { opaqueData, tier: tierData.id };
      } else {
        endpoint = '/api/payment/topup';
        body = { opaqueData, amount: tierData.amount };
      }

      const response = await apiClient.post(endpoint, body);

      if (response.data.success) {
        setSuccess(true);
        toast({
          title: 'Payment Successful!',
          description: response.data.message,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Callback to update parent state
        if (onSuccess) {
          onSuccess(response.data);
        }

        // Close after short delay so user sees the success state
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        throw new Error(response.data.error || 'Payment failed');
      }

    } catch (err) {
      console.error('[Payment] Error:', err);
      setError(err.response?.data?.error || err.message || 'Payment processing failed. Please try again.');
      setIsProcessing(false);
    }
  };

  if (!tierData) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" closeOnOverlayClick={!isProcessing}>
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white">
        <ModalHeader borderBottomWidth="1px" borderColor="gray.700">
          <HStack spacing={3}>
            <Icon as={FaLock} color="green.400" boxSize={5} />
            <Text>Secure Checkout</Text>
          </HStack>
        </ModalHeader>
        {!isProcessing && <ModalCloseButton />}

        <ModalBody py={6}>
          <VStack spacing={5} align="stretch">

            {/* Order Summary */}
            <Box bg="gray.700" p={4} borderRadius="md" borderWidth="1px" borderColor={`${tierData.color}.500`}>
              <HStack justify="space-between" align="center">
                <VStack align="start" spacing={1}>
                  <Text fontWeight="bold" fontSize="lg" color={`${tierData.color}.200`}>
                    {tierData.name} {paymentType === 'subscribe' ? 'Monthly Contribution' : 'Contribution'}
                  </Text>
                  <HStack spacing={2}>
                    <Icon as={FaCoins} color="yellow.400" boxSize={4} />
                    <Text fontSize="sm" color="gray.300">
                      +{tierData.muAllocation} Member Units
                    </Text>
                  </HStack>
                </VStack>
                <VStack align="end" spacing={0}>
                  <Text fontSize="2xl" fontWeight="black">${tierData.amount}</Text>
                  {paymentType === 'subscribe' && (
                    <Text fontSize="xs" color="gray.400">/month</Text>
                  )}
                </VStack>
              </HStack>
            </Box>

            {/* Success State */}
            {success && (
              <Alert status="success" borderRadius="md" bg="green.800">
                <AlertIcon />
                <Text>Payment processed successfully!</Text>
              </Alert>
            )}

            {/* Error State */}
            {error && (
              <Alert status="error" borderRadius="md" bg="red.900">
                <AlertIcon />
                <Text fontSize="sm">{error}</Text>
              </Alert>
            )}

            {!success && (
              <>
                <Divider borderColor="gray.600" />

                {/* Card Number */}
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.300">
                    <HStack spacing={2}>
                      <Icon as={FaCreditCard} />
                      <Text>Card Number</Text>
                    </HStack>
                  </FormLabel>
                  <Input
                    value={cardNumber}
                    onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                    placeholder="4111 1111 1111 1111"
                    bg="gray.700"
                    borderColor="gray.600"
                    _hover={{ borderColor: 'gray.500' }}
                    _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px #4299E1' }}
                    maxLength={19}
                    inputMode="numeric"
                    autoComplete="cc-number"
                    isDisabled={isProcessing}
                  />
                </FormControl>

                {/* Expiry + CVV Row */}
                <HStack spacing={4}>
                  <FormControl flex={1}>
                    <FormLabel fontSize="sm" color="gray.300">Month</FormLabel>
                    <Input
                      value={expMonth}
                      onChange={(e) => setExpMonth(e.target.value.replace(/\D/g, '').substring(0, 2))}
                      placeholder="MM"
                      bg="gray.700"
                      borderColor="gray.600"
                      _hover={{ borderColor: 'gray.500' }}
                      _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px #4299E1' }}
                      maxLength={2}
                      inputMode="numeric"
                      autoComplete="cc-exp-month"
                      isDisabled={isProcessing}
                    />
                  </FormControl>
                  <FormControl flex={1}>
                    <FormLabel fontSize="sm" color="gray.300">Year</FormLabel>
                    <Input
                      value={expYear}
                      onChange={(e) => setExpYear(e.target.value.replace(/\D/g, '').substring(0, 4))}
                      placeholder="YYYY"
                      bg="gray.700"
                      borderColor="gray.600"
                      _hover={{ borderColor: 'gray.500' }}
                      _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px #4299E1' }}
                      maxLength={4}
                      inputMode="numeric"
                      autoComplete="cc-exp-year"
                      isDisabled={isProcessing}
                    />
                  </FormControl>
                  <FormControl flex={1}>
                    <FormLabel fontSize="sm" color="gray.300">CVV</FormLabel>
                    <Input
                      value={cvv}
                      onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').substring(0, 4))}
                      placeholder="123"
                      type="password"
                      bg="gray.700"
                      borderColor="gray.600"
                      _hover={{ borderColor: 'gray.500' }}
                      _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px #4299E1' }}
                      maxLength={4}
                      inputMode="numeric"
                      autoComplete="cc-csc"
                      isDisabled={isProcessing}
                    />
                  </FormControl>
                </HStack>

                {/* Pay Button */}
                <Button
                  colorScheme="green"
                  size="lg"
                  w="full"
                  height="14"
                  fontSize="lg"
                  fontWeight="bold"
                  onClick={processPayment}
                  isLoading={isProcessing}
                  loadingText="Processing..."
                  leftIcon={!isProcessing ? <Icon as={FaLock} /> : undefined}
                  _hover={{ bg: 'green.500', transform: 'translateY(-1px)' }}
                  _active={{ bg: 'green.600' }}
                  transition="all 0.2s"
                  isDisabled={isProcessing}
                >
                  {paymentType === 'subscribe' 
                    ? `Contribute — $${tierData.amount}/mo`
                    : `Contribute $${tierData.amount}`
                  }
                </Button>

                {/* Security Notice */}
                <HStack justify="center" spacing={2} pt={1}>
                  <Icon as={FaLock} color="green.400" boxSize={3} />
                  <Text fontSize="xs" color="gray.500">
                    Secured by Authorize.net. Card data never touches our servers.
                  </Text>
                </HStack>
              </>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AcceptJSPaymentForm;
